import React, { useEffect, useMemo, useState } from 'react'
import { Accordion } from 'primereact/accordion'
import { Column } from 'primereact/column'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'

import {
  Btn,
  Cnpj,
  CustomAccordionTab,
  CustomDataTable,
  CustomModal,
  CustomPanel,
  InputText,
  PageHeader,
} from '../../../../components'
import { tableCellCentered } from '../../../../assets/js/styles'
import {
  hideLoadingBox,
  showLoadingBox,
  showMessageBox,
} from '../../../../store/actions/system.actions'
import useRelatorioPosicoesEtiquetasCliente from './RelatorioPosicoesEtiquetasClienteHook'

const linkButtonStyle = {
  border: 'none',
  background: 'transparent',
  color: '#14B0BF',
  cursor: 'pointer',
  fontWeight: 600,
  padding: 0,
}

const normalizeFilterText = value =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]/g, '')
    .toLowerCase()

const getClienteLabel = cliente => {
  if (!cliente) return ''
  return cliente.cnpj ? (
    <>
      {cliente.cliente} - CNPJ/CPF: <Cnpj>{cliente.cnpj}</Cnpj>
    </>
  ) : (
    cliente.cliente
  )
}

const etiquetaMatchesFilter = (etiqueta, filter) => {
  if (!filter) return true
  return [etiqueta.pallet_area_id, etiqueta.estoque, etiqueta.endereco].some(value =>
    normalizeFilterText(value).includes(filter),
  )
}

function TabelaEtiquetas({ etiquetas, resetKey }) {
  return (
    <CustomDataTable
      key={resetKey}
      value={etiquetas || []}
      emptyMessage="Nenhuma etiqueta encontrada"
      rows={10}
      paginator={(etiquetas || []).length > 10}
    >
      <Column field="pallet_area_id" header="Etiqueta" style={tableCellCentered(130)} />
      <Column field="estoque" header="Estoque" style={tableCellCentered(220)} />
      <Column field="endereco" header="Posição de Estoque" />
    </CustomDataTable>
  )
}

function ModalEtiquetasPorNota({ show, cliente, deliveries, onClose }) {
  const [filter, setFilter] = useState('')

  useEffect(() => {
    if (!show) setFilter('')
  }, [show])

  const normalizedFilter = normalizeFilterText(filter)
  const filteredDeliveries = useMemo(() => {
    if (!normalizedFilter) return deliveries || []

    return (deliveries || []).filter(delivery => {
      const headerMatches = [delivery.identifier, delivery.company_name].some(value =>
        normalizeFilterText(value).includes(normalizedFilter),
      )
      const etiquetaMatches = (delivery.etiquetas || []).some(etiqueta =>
        etiquetaMatchesFilter(etiqueta, normalizedFilter),
      )

      return headerMatches || etiquetaMatches
    })
  }, [deliveries, normalizedFilter])

  return (
    <CustomModal
      header="Etiquetas por Nota"
      isVisible={show}
      onClose={onClose}
      width={1000}
      maximizable={true}
      contentStyle={{ maxHeight: '75vh', overflowY: 'auto' }}
    >
      {cliente ? <h4 style={{ marginTop: 0 }}>{getClienteLabel(cliente)}</h4> : null}
      <div style={{ marginBottom: 12 }}>
        <InputText
          label="Filtro"
          value={filter}
          onChange={setFilter}
          placeholder="Identificador, empresa, etiqueta, estoque ou posição"
          marginTop={0}
        />
      </div>
      {filteredDeliveries.length ? (
        <Accordion>
          {filteredDeliveries.map(delivery => (
            <CustomAccordionTab
              key={delivery.delivery_id}
              header={`Identificador: ${delivery.identifier} - Empresa: ${delivery.company_name} - ${delivery.etiquetas.length} etiqueta(s)`}
            >
              <TabelaEtiquetas etiquetas={delivery.etiquetas} />
            </CustomAccordionTab>
          ))}
        </Accordion>
      ) : (
        <p>Nenhuma NF encontrada</p>
      )}
    </CustomModal>
  )
}

function ModalEtiquetasPorCliente({ show, cliente, etiquetas, onClose }) {
  const [filter, setFilter] = useState('')

  useEffect(() => {
    if (!show) setFilter('')
  }, [show])

  const normalizedFilter = normalizeFilterText(filter)
  const filteredEtiquetas = useMemo(
    () => (etiquetas || []).filter(etiqueta => etiquetaMatchesFilter(etiqueta, normalizedFilter)),
    [etiquetas, normalizedFilter],
  )

  return (
    <CustomModal
      header="Etiquetas por Cliente"
      isVisible={show}
      onClose={onClose}
      width={1000}
      maximizable={true}
      contentStyle={{ maxHeight: '75vh', overflowY: 'auto' }}
    >
      {cliente ? <h4 style={{ marginTop: 0 }}>{getClienteLabel(cliente)}</h4> : null}
      <div style={{ marginBottom: 12 }}>
        <InputText
          label="Filtro"
          value={filter}
          onChange={setFilter}
          placeholder="Etiqueta, estoque ou posição"
          marginTop={0}
        />
      </div>
      <TabelaEtiquetas etiquetas={filteredEtiquetas} resetKey={normalizedFilter} />
    </CustomModal>
  )
}

function RelatorioPosicoesEtiquetasCliente({ history, dispatch, selectedTp }) {
  const { state, localDispatch } = useRelatorioPosicoesEtiquetasCliente()
  const filtroCliente = normalizeFilterText(state.clienteFilter)
  const clientes = state.clientes.filter(cliente => {
    const clienteText = normalizeFilterText(cliente.cliente)
    const cnpjText = normalizeFilterText(cliente.cnpj)
    return clienteText.includes(filtroCliente) || cnpjText.includes(filtroCliente)
  })

  useEffect(() => {
    localDispatch.getRelatorio(selectedTp)
  }, [selectedTp])

  useEffect(() => {
    if (state.loadingMessage) {
      dispatch(showLoadingBox(state.loadingMessage, 'Aguarde . . .'))
    } else {
      dispatch(hideLoadingBox())
    }
  }, [state.loadingMessage])

  useEffect(() => {
    if (state.errorMessage) {
      dispatch(showMessageBox(null, state.errorMessage))
      localDispatch.resetState()
    }
  }, [state.errorMessage])

  return (
    <>
      <PageHeader>8.23 - Relatório de Posições de Etiquetas por Cliente</PageHeader>
      <div style={{ marginTop: 20 }}>
        <CustomPanel onVoltar={history.goBack} showBtnNovo={false}>
          <div className="p-grid">
            <div className="p-col-4">
              <InputText
                label="Filtro"
                value={state.clienteFilter}
                onChange={localDispatch.onClienteFilterChange}
                placeholder="Digite o cliente ou CNPJ/CPF"
                marginTop={0}
              />
            </div>
            <div className="p-col-2" style={{ display: 'flex', alignItems: 'flex-end' }}>
              <Btn
                onClick={() => localDispatch.onBtnExportarClick(selectedTp)}
                disabled={!state.clientes.length}
              >
                Exportar
              </Btn>
            </div>
            <div className="p-col-12">
              <CustomDataTable
                key={filtroCliente}
                value={clientes}
                emptyMessage="Nenhum cliente encontrado"
                rows={15}
                paginator={true}
              >
                <Column field="cliente" header="Cliente" />
                <Column
                  field="cnpj"
                  header="CNPJ/CPF"
                  style={tableCellCentered(160)}
                  body={cliente => <Cnpj>{cliente.cnpj}</Cnpj>}
                />
                <Column
                  header="Qtd NF"
                  style={tableCellCentered(130)}
                  body={cliente => (
                    <button
                      type="button"
                      style={linkButtonStyle}
                      onClick={() => localDispatch.onQtdNfClicked(cliente, selectedTp)}
                    >
                      {cliente.qtd_nf}
                    </button>
                  )}
                />
                <Column
                  header="Qtd Etiquetas"
                  style={tableCellCentered(150)}
                  body={cliente => (
                    <button
                      type="button"
                      style={linkButtonStyle}
                      onClick={() => localDispatch.onQtdEtiquetasClicked(cliente, selectedTp)}
                    >
                      {cliente.qtd_etiquetas}
                    </button>
                  )}
                />
              </CustomDataTable>
            </div>
          </div>
        </CustomPanel>
      </div>
      <ModalEtiquetasPorNota
        show={state.modalNotas.show}
        cliente={state.modalNotas.cliente}
        deliveries={state.modalNotas.deliveries}
        onClose={localDispatch.onModalNotasClose}
      />
      <ModalEtiquetasPorCliente
        show={state.modalEtiquetas.show}
        cliente={state.modalEtiquetas.cliente}
        etiquetas={state.modalEtiquetas.etiquetas}
        onClose={localDispatch.onModalEtiquetasClose}
      />
    </>
  )
}

const mapStateToProps = ({ system: { selectedTp } }) => ({ selectedTp })
export default connect(mapStateToProps)(withRouter(RelatorioPosicoesEtiquetasCliente))
